/**
 * Act 1, beat 6 — ***The Loading Yard***.
 *
 * Spec §7.6, re-cut. The bullet as written is three beats wearing one number —
 * see `docs/story-review.md` S-3 — so **everything from "rolling escape" onward
 * is beat 7**. What is left here is the storm, Kestner, the truck and the flash,
 * and it ends with her under the trailer.
 *
 * ── The one sentence this beat has to deliver ────────────────────────────────
 *
 * **The first time the sky opens, she is standing in the middle of the yard, and
 * the whole world can see her — and so can he, because the flash is what makes
 * him look up.**
 *
 * Five beats have taught light as a property of a *place*: pools under battens,
 * unlit bays, a corridor with no dark, a room with one red lamp. The yard takes
 * that away and makes light a property of *time*. The dark is not somewhere she
 * goes; it is something she is between.
 *
 * ── The flash and the watcher are ONE event ──────────────────────────────────
 *
 * Spec §5.1: *"every detection must be telegraphed and explainable after the
 * fact."* A storm is irregular, and irregular is its whole character; a
 * metronome would be learnable, which would make this a puzzle. The resolution
 * is the one real lightning already has — **the leader flicker**, 0.55 s before
 * the strike (`STORM.tell` in `src/systems/storm.ts`).
 *
 * Kestner's head comes up **on the tell, not on the strike**. So the sky tells
 * the player two things in one signal and half a second in advance: *you are
 * about to be lit*, and *he is about to be looking*. Nothing about this needs a
 * HUD and nothing about it is unfair.
 *
 * ── Every stage has a backstop, and stage 1's is the one that matters ────────
 *
 * Beat 5 shipped a `waitUntil` on its **opening** stage, hung on a flag raised
 * by walking, and a player who pressed nothing sat in the entrance for the rest
 * of the session (D-097). It also shipped with the gate that would have caught
 * that, unrun. So: every stage below ends on a clock as well as on its
 * condition, `yard-beat.spec.ts`'s no-input run is what says so, and that spec
 * was written **before** stage 1 existed rather than after it.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `src/world/yardDirector.ts` is the only file that knows
 * any of this is triangles, weather or a man. Same rule as every beat since
 * `born.ts`: if a stage in this file ever imports Three, the beat has stopped
 * being replayable in Node and the mistake will be here rather than in the
 * sequencer. The spatial stages wait on flags the *room* raises, for the reason
 * `otto.ts` gives — "she is clear of the bins" is a fact about a room.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactable ids. There is one, and it is offered rather than required.
 *
 * `observe` on Kestner is meant to be where the mental map takes its first note
 * (`docs/story-review.md` S-2). The map itself shipped in session 33
 * (`src/story/ledger.ts` + `src/ui/mentalMap.ts`, D-136/D-137), but this beat has
 * not yet been wired to write into it — beat 6's job is to make sure there is
 * something for it to have drawn, once it is. Holding focus on him composes the
 * shot the beat is named for: a tired man in a flood of light, making marks on a
 * board, while animals go up a race behind him.
 *
 * It completes stage 2 early, and stage 2 ends without it on a clock. A player
 * who never looks has not failed anything; they have simply not been told who
 * this is, which is a cost the beat is willing to pay to avoid insisting.
 */
export const YARD_IDS = {
  kestner: 'yard.kestner',
} as const;

/** Story flags the GAME raises. All of them are things only the simulation knows. */
export const YARD_FLAGS = {
  /**
   * She is out of the bin and standing on the apron.
   *
   * Raised by the director once it has actually set her down and given the
   * controller its body back — **not** when the climb-out animation starts.
   * D-090's rule for the fourth time: a flag describes what the world has
   * finished doing, never what it has begun.
   */
  outOfTheBin: 'yard.outOfTheBin',
  /** Two metres clear of the bin row, which is the first thing she chooses to do. */
  clearOfBins: 'yard.clearOfBins',
  /** She has held `observe` on him once. */
  watchedHim: 'yard.watchedHim',
  /** The teach flash has been struck and has fully decayed. */
  flashTaught: 'yard.flashTaught',
  /** She is in the trailer's shadow — the beat's destination. */
  underTheTrailer: 'yard.underTheTrailer',
} as const;

/** Every cue this beat can emit. The director switches on these and nothing else. */
export const YARD_CUES = {
  /**
   * Rain on a steel lid, from inside a bin, and the lid is ajar.
   *
   * The camera is in the bin looking up at a strip of sky, which is the mirror
   * of the shot beat 5 ended on: she went in under a lid and she comes out from
   * under one. It costs nothing — beat 5's ride camera already proved the
   * framing — and it is the only place in the game where the player sees the
   * weather before they see the room.
   */
  open: 'yard.open',
  /** She climbs out. The director owns her body until it has set her down. */
  climbOut: 'yard.climbOut',
  /**
   * Across the yard: headlights, the race, animals going up it, and a man
   * standing in the light making marks on a board.
   *
   * The animals start moving here. **Nothing is dangerous yet** — his cone is on
   * the race, which is the one direction she never needs to cross.
   */
  theCount: 'yard.theCount',
  /** `observe` becomes available on him. Offered once, required never. */
  offerKestner: 'yard.offerKestner',
  /**
   * The teach. One authored strike, fired while she is still behind the bin row
   * where being lit costs nothing.
   *
   * **It is a demonstration and she is in the audience.** It cannot be failed:
   * capture is not live until the next stage. What the player is being shown is
   * not the flash — it is his head coming up during it, and going back down
   * afterwards.
   */
  teachFlash: 'yard.teachFlash',
  /**
   * The crossing proper, and the storm starts keeping its own irregular time.
   *
   * **Capture goes live here and only here.**
   */
  crossing: 'yard.crossing',
  /**
   * Put her under the trailer, whether or not she crossed.
   *
   * The anti-softlock, and the beat is not embarrassed by it. See
   * `CROSS_PATIENCE`.
   */
  putHerUnder: 'yard.putHerUnder',
  /** Snout height, under eight metres of steel, looking out at boots and wheels. */
  underIt: 'yard.underIt',
  /** Fade to black. */
  fadeOut: 'yard.fadeOut',
  /** The beat is over — and the storm stops being simulated. */
  end: 'yard.end',
} as const;

/**
 * How long the beat waits for a player who has not climbed out and moved.
 *
 * Twenty-five seconds, and it is generous on purpose: **this is the first time
 * in the game the player can see the sky**, and rule 5 of the plan is that they
 * are given thirty seconds of simply standing in it. A player looking up is
 * playing the beat correctly, and a stage that hurried them off the apron would
 * be spending the one thing the first exterior has that no interior did.
 *
 * It is also the stage beat 5 got wrong. See the header.
 */
const OUT_PATIENCE = 25;

/** How long `observe` is offered before the beat stops waiting to be looked at. */
const COUNT_PATIENCE = 40;

/**
 * How long the teach flash gets before the beat gives up on it.
 *
 * The envelope is 1.65 s from the tell (`STORM.decayStart + decayDuration`), and
 * his sweep runs about a second and a half past it, so eight seconds is roughly
 * three times the length of the thing being waited for. It exists because
 * `flashTaught` is raised by the *world* — the director watching its own storm
 * decay — and D-090 is the standing rule that a stage waiting on the world holds
 * a backstop as well as a flag.
 */
const FLASH_PATIENCE = 8;

/**
 * How long the crossing may take before the beat stops waiting.
 *
 * **This section was WRONG in the first draft of `docs/beat-6-plan.md` and is
 * kept as a worked example.** The draft said this stage must have no patience
 * clock — "it is the beat, and a player standing still in cover is playing it
 * correctly" — and then demanded a spec that plays the beat with no input at all
 * and reaches `end`. Those cannot both be true: spawn is the bin row at z −6.5,
 * this stage ends twenty-two metres away under the trailer, and a player who
 * presses nothing never arrives. The gate would have been unwritable, or quietly
 * cheated with scripted movement — D-097's exact defect, inside the document
 * that cites D-097 twice. Found on independent review before a line was built.
 *
 * **Two different objects were being conflated.** A *punishing* clock — one that
 * fails the stage or moves her backwards for waiting — would indeed be wrong,
 * because the whole point of the storm is that waiting in cover is correct play.
 * A *backstop* — one that ends the beat for a player who is never going to
 * finish it — is not that object, and beat 5 already pays this constant for its
 * bin (D-097).
 *
 * 150 s is roughly nine flashes at the storm's mean interval, which is far past
 * any honest reading of "she is waiting for a gap". It is invisible to a player
 * who is playing and decisive for one who is not. It costs a constant; a
 * softlock costs the game (spec §10).
 */
const CROSS_PATIENCE = 150;

/** The player has six seconds under the trailer before the final fade begins. */
const UNDER_IT_SECONDS = 6;

/** The final fade has four fixed-step seconds before the beat emits `end`. */
const CLOSE_SECONDS = 4;

/**
 * The complete no-input route through beat 6, from its first fixed tick to end.
 *
 * Kept beside the six authored stage clocks rather than recreated in the spec:
 * a test budget that drifts from the beat can pass after the player-visible
 * backstop was shortened, or fail after it was lengthened, and then reports a
 * harness disagreement as a story failure (D-132).
 */
export const LOADING_YARD_NO_INPUT_SECONDS =
  OUT_PATIENCE + COUNT_PATIENCE + FLASH_PATIENCE + CROSS_PATIENCE + UNDER_IT_SECONDS + CLOSE_SECONDS;

/** A stage that is purely a length of time with cues at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, patience: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

export const LOADING_YARD_BEAT: Beat = {
  number: 6,
  id: 'act1.loadingYard',
  stages: [
    // ── out of the bin ───────────────────────────────────────────────────────
    // Rain on a steel lid, from inside. The lid is ajar and there is a strip of
    // sky in it. She climbs out into the yard and the sky is above her for the
    // first time in the game.
    //
    // **No flash yet, no watcher yet.** The storm is audible — it has been
    // raining since before the fade came up — but it does not strike until stage
    // 3, so the player's first look at an exterior is not competing with an
    // event. There has been no sky since the title card and it is worth thirty
    // seconds of nothing happening.
    waitFor('out-of-the-bin', YARD_FLAGS.clearOfBins, OUT_PATIENCE, YARD_CUES.open, YARD_CUES.climbOut),

    // ── the count ────────────────────────────────────────────────────────────
    // Across the yard: two headlights pointed at her side of it, a stock race
    // with animals going up it, and a man standing in a flood of light making
    // marks on a board.
    //
    // He is almost harmless here, on purpose, for about forty seconds. His cone
    // is narrow and it is pointed at the race — the one direction she never
    // needs to cross — and the player is meant to watch him for long enough to
    // believe that, because stage 3 is about to take it away.
    waitFor('the-count', YARD_FLAGS.watchedHim, COUNT_PATIENCE, YARD_CUES.theCount, YARD_CUES.offerKestner),

    // ── the first flash ──────────────────────────────────────────────────────
    // The teach, and it is the only strike in the beat that is authored rather
    // than sampled. The tell, the strike, the yard white for a heartbeat — **and
    // his head comes up.**
    //
    // Staged so it cannot be failed: it fires while she is still behind the bin
    // row, where being lit costs nothing, and capture is not live until the next
    // stage. The lesson is not "hide"; the lesson is that the sky and the man
    // are the same event, and that it announces itself half a second early.
    waitFor('the-first-flash', YARD_FLAGS.flashTaught, FLASH_PATIENCE, YARD_CUES.teachFlash),

    // ── the crossing ─────────────────────────────────────────────────────────
    // Twenty-two metres of open yard, five pallet groups, one flood, two
    // headlight cones and a sky that opens every 7–16 s. **Capture is live.**
    //
    // Nothing here is a chase (plan rule 5). If it plays thin, the levers are the
    // flash interval and the length of his sweep — never a second watcher. A
    // second cone in a room with 0.45 haze and no walls is a draw-call bill and a
    // fairness problem at once.
    {
      ...waitFor('the-crossing', YARD_FLAGS.underTheTrailer, CROSS_PATIENCE, YARD_CUES.crossing),
      exit: (ctx, emit): void => {
        if (!ctx.completed.has(YARD_FLAGS.underTheTrailer)) emit(YARD_CUES.putHerUnder);
      },
    },

    // ── under it ─────────────────────────────────────────────────────────────
    // The camera drops to snout height (§4's `crouch-low` framing) and holds on
    // the yard from underneath: rain on concrete, boots, wheels, and the sound of
    // the weather changing because there is steel over her.
    //
    // Six seconds, and they are the reward for the crossing rather than a
    // transition out of it.
    hold('under-it', UNDER_IT_SECONDS, YARD_CUES.underIt),

    // ── out ──────────────────────────────────────────────────────────────────
    // A sixth stage where the plan's table has five, for the reason beat 5 has
    // one: a fade is a length of time, and a `fadeOut` emitted from the exit of
    // the stage before it would have no frames to happen in.
    {
      ...hold('close', CLOSE_SECONDS, YARD_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(YARD_CUES.end),
    },
  ],
};
